/// <reference lib="dom" />
/**
 * audioManager.ts
 * The only entry point for playing audio. Nothing else in the codebase may
 * touch an `HTMLAudioElement` or the Web Audio graph directly (AGENTS.md:
 * audio is dispatched through this service, never from the game loop).
 *
 * Sources are injected by the web asset layer via {@link registerAudioSources}
 * so this module stays free of bundler-specific asset imports and remains
 * unit-testable without a bundler.
 *
 * Playback is fire-and-forget: every call returns synchronously and failures
 * are logged, never thrown, so a blocked or missing sound can never stall a
 * frame or bubble into the engine tick.
 *
 * @module audioManager
 */

import Logger from '../utils/logger';
import type { AudioSourceRegistry, MusicId, SfxId } from '../types/audio';
import { MAX_SFX_VOICES } from '../../constants/audio';
import {
  ensureAudioGraph,
  getBusVolume,
  resumeAudioGraph,
  routeElement,
  setBusVolume,
} from './audioGraph';

let _sources: AudioSourceRegistry = { sfx: {}, music: {} };

/** Voice pool per sound effect, grown on demand up to MAX_SFX_VOICES. */
const _voices = new Map<SfxId, HTMLAudioElement[]>();

let _musicEl: HTMLAudioElement | null = null;
let _musicId: MusicId | null = null;
let _musicRouted = false;
let _unlocked = false;

/** Gesture events that satisfy a browser's autoplay policy. */
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Register the bundled audio URLs. Call once at boot, before any playback.
 *
 * @param sources Sfx and music URL tables produced by the bundler.
 */
export function registerAudioSources(sources: AudioSourceRegistry): void {
  _sources = sources;
  Logger.debug(
    'AudioManager',
    `Registered ${String(Object.keys(sources.sfx).length)} sfx, ` +
      `${String(Object.keys(sources.music).length)} music`,
  );
}

/** Create an audio element for a URL, or null when the platform lacks one. */
function _createElement(url: string): HTMLAudioElement | null {
  if (typeof globalThis.Audio !== 'function') return null;
  try {
    const el = new globalThis.Audio(url);
    el.preload = 'auto';
    return el;
  } catch (err) {
    Logger.warn('AudioManager', 'Failed to create audio element', err);
    return null;
  }
}

/** Start an element, swallowing autoplay rejections and jsdom stubs. */
function _play(el: HTMLAudioElement, label: string): void {
  try {
    const p: unknown = el.play();
    if (p instanceof Promise) {
      p.catch((err: unknown) => Logger.debug('AudioManager', `"${label}" blocked`, err));
    }
  } catch (err) {
    Logger.debug('AudioManager', `"${label}" failed to start`, err);
  }
}

/**
 * Unlock audio. Must be called from a real user-gesture handler: browsers
 * refuse to start an `AudioContext` otherwise. Idempotent.
 */
export function unlockAudio(): void {
  if (_unlocked) {
    resumeAudioGraph();
    return;
  }
  _unlocked = true;
  ensureAudioGraph();
  resumeAudioGraph();
  Logger.debug('AudioManager', 'Audio unlocked');
}

/**
 * Listen for the first user gesture and unlock audio when it arrives.
 *
 * @param target Event source; defaults to the document.
 * @returns A teardown function that removes the listeners early.
 */
export function installAudioUnlock(target?: EventTarget): () => void {
  const source: EventTarget | undefined = target ?? globalThis.document;
  if (source === undefined) return () => undefined;

  const handler = (): void => {
    unlockAudio();
    teardown();
  };
  const teardown = (): void => {
    for (const name of UNLOCK_EVENTS) source.removeEventListener(name, handler);
  };
  for (const name of UNLOCK_EVENTS) {
    source.addEventListener(name, handler, { passive: true });
  }
  return teardown;
}

/** Find a free voice for a sound, growing or stealing from the pool. */
function _acquireVoice(id: SfxId, url: string): HTMLAudioElement | null {
  let ring = _voices.get(id);
  if (ring === undefined) {
    ring = [];
    _voices.set(id, ring);
  }
  const free = ring.find((el) => el.paused || el.ended);
  if (free !== undefined) return free;

  if (ring.length < MAX_SFX_VOICES) {
    const el = _createElement(url);
    if (el === null) return null;
    ring.push(el);
    return el;
  }
  // DECISION: Steal voice 0 rather than dropping the cue. A retriggered sound
  // that goes silent reads as a bug; a clipped tail does not.
  return ring[0] ?? null;
}

/**
 * Play a sound effect. Overlapping calls allocate additional voices up to
 * `MAX_SFX_VOICES`. Unknown ids warn and return without throwing.
 *
 * @param id Sound effect to play.
 */
export function playSfx(id: SfxId): void {
  const url = _sources.sfx[id];
  if (url === undefined) {
    Logger.warn('AudioManager', `No source registered for sfx "${id}"`);
    return;
  }
  const el = _acquireVoice(id, url);
  if (el === null) return;

  // Routed elements take their level from the sfx gain node, so the element
  // itself must stay at unity or the volume would be applied twice.
  el.volume = routeElement(el, 'sfx') ? 1 : getBusVolume('sfx');
  try {
    el.currentTime = 0;
  } catch {
    // Not seekable until metadata loads; playing from the current position
    // is preferable to dropping the cue.
  }
  _play(el, id);
}

/**
 * Lazily create the single, long-lived music element.
 *
 * DECISION: one element for all music, swapping `src`, rather than one per
 * track. `createMediaElementSource` can only be called once per element, so a
 * per-track element would strand a source node in the graph on every level
 * change — each one holding a strong reference to a dead audio element.
 */
function _ensureMusicElement(): HTMLAudioElement | null {
  if (_musicEl !== null) return _musicEl;
  const el = _createElement('');
  if (el === null) return null;
  el.loop = true;
  _musicEl = el;
  return el;
}

/**
 * Play a looping music track, replacing whatever is playing. Re-requesting the
 * track that is already playing is a no-op so level reloads don't restart it.
 *
 * @param id Track to play.
 */
export function playMusic(id: MusicId): void {
  const url = _sources.music[id];
  if (url === undefined) {
    Logger.warn('AudioManager', `No source registered for music "${id}"`);
    return;
  }
  if (_musicId === id && _musicEl !== null && !_musicEl.paused) return;

  const el = _ensureMusicElement();
  if (el === null) return;

  if (_musicId !== id) el.src = url;
  _musicId = id;
  _musicRouted = routeElement(el, 'music');
  el.volume = _musicRouted ? 1 : getBusVolume('music');
  _play(el, id);
}

/** Stop the current music track. The element is kept for the next track. */
export function stopMusic(): void {
  _musicId = null;
  if (_musicEl === null) return;
  try {
    _musicEl.pause();
    _musicEl.currentTime = 0;
  } catch (err) {
    Logger.debug('AudioManager', 'Failed to stop music', err);
  }
}

/**
 * Set music volume.
 *
 * @param value Volume in the 0–1 range; out-of-range input is clamped.
 */
export function setMusicVolume(value: number): void {
  setBusVolume('music', value);
  // An unrouted element is not fed by the gain node, so it needs the level
  // written straight onto it — music outlives a volume change, unlike sfx.
  if (_musicEl !== null && !_musicRouted) _musicEl.volume = getBusVolume('music');
}

/**
 * Set sound-effect volume. Applies to voices started after this call.
 *
 * @param value Volume in the 0–1 range; out-of-range input is clamped.
 */
export function setSfxVolume(value: number): void {
  setBusVolume('sfx', value);
}

/** Id of the track currently playing, or null when music is stopped. */
export function getCurrentMusicId(): MusicId | null {
  return _musicId;
}

/** Reset all manager state. Test-only helper. */
export function _resetAudioManagerForTesting(): void {
  stopMusic();
  _musicEl = null;
  _musicRouted = false;
  _voices.clear();
  _sources = { sfx: {}, music: {} };
  _unlocked = false;
}
